// src/i18n.js
import { createI18n } from 'vue-i18n';

const storedLocale = typeof window !== 'undefined'
  ? window.localStorage.getItem('preferredLocale')
  : null;
const defaultLocale = storedLocale || 'fr';

const i18n = createI18n({
  legacy: false, // You can enable or disable legacy mode (for Vue 3 setup)
  locale: defaultLocale,  // Default language
  fallbackLocale: 'fr', // Fallback language when a key is missing
  messages: {
    en: {
      
      HomeComponent: {
        welcome: 'Welcome to my app!',
        heroTagline: "I'm Christopher-William",
        about: 'About me',
        aboutMeMessage: `My name is Christopher-William, and I’ve been passionate about technology and electronics since the age of 12. What started as a curiosity for how things work quickly turned into a hands-on obsession—I spent my early years taking apart devices, learning electronics fundamentals, and building small circuits, all driven by a desire to create and understand.
                          Now {age} years old, I’m pursuing a degree in Software Engineering, where I merge my interests in hardware and software to develop robust, scalable, and intelligent systems. My journey into programming began with Java, creating custom Minecraft servers and mods. Through that, I learned object-oriented programming, client-server architecture, networking concepts, and how to design engaging, user-driven experiences—all within a real, interactive environment used by players around the world.
                          Since then, I’ve continuously challenged myself to grow and explore new domains. I’ve worked across a wide range of technologies, including full-stack web development, embedded systems, database design, RESTful APIs, DevOps practices, and artificial intelligence. I thrive on building tools and solutions that solve real-world problems, and I enjoy balancing performance, maintainability, and elegant design.
                          Above all, I’m driven by a constant hunger to learn, improve, and innovate. Whether I’m contributing to open-source projects, automating workflows, or diving into new programming languages and frameworks, I’m always looking for the next opportunity to push my limits and bring ideas to life.`,
        myAmbitions: 'My ambitions',
        myAmbitionsMessage: `I strive to contribute to meaningful, high-impact projects in the field of computer science—whether it’s through developing innovative software solutions, optimizing complex systems, or solving real-world problems with technology. My goal is to not only apply my current skill set effectively but to constantly expand it by learning new tools, frameworks, and paradigms. I’m deeply committed to continuous improvement, staying up-to-date with industry trends, and embracing every opportunity to grow as a developer, engineer, and problem solver.`,
        studies: 'Studies',
        andreLaurendeau: {
          message: `I completed a Computer Science Technology program at Cégep André-Laurendeau, specializing in back-end development while also expanding my skills in full-stack development. This program gave me hands-on experience with modern technologies on both the client and server sides, and helped me build a strong foundation in software architecture, advanced algorithms, and efficient database management.`,
          ul: {
            program: 'Program: Computer Science Technology',
            specialization: 'Specialization: Back-End and Full-Stack Development',
            keyCourses: `Key Courses: Web development with Angular, React, and Thymeleaf; Mobile development with Flutter; Server-side programming with Spring Boot, Python, and Kotlin; Secure authentication with JWT; Relational database management with MySQL and H2; Cloud services integration with Firebase`,
            projects: `Notable Projects: A full-featured time management web application and a redesigned internship selection system for the college using Spring Boot, React, and MySQL; A cross-platform mobile app built with Flutter and Firebase; Secure authentication system using JWT; Dynamic user interfaces with Angular and integration of secure RESTful APIs.`,
          },
          gradMessage: 'Graduation Date:',
          gradDate: ' May 2024',
        },
        ets: { 
          message: `I am currently enrolled in the Bachelor's program in Software Engineering at ÉTS (École de technologie supérieure), where I focus on advanced software design, system architecture, and algorithm optimization. Driven by a strong interest in artificial intelligence and cybersecurity, I am actively developing solid expertise in these two critical and fast-evolving areas of technology. This program also allows me to deepen my knowledge of cloud computing, distributed systems, and modern application security.`,
          ul: {
            program: `Program: Bachelor's in Software Engineering`,
            keyCourses: `Key Areas: Artificial Intelligence, Cybersecurity, Advanced Software Design, Cloud Computing, Distributed Systems`,
            projects: `Upcoming Projects: Development of a secure, cloud-based collaborative task management tool, featuring real-time synchronization and integrated data protection mechanisms.`,
          },
          gradMessage: 'Start Date:',
          gradDate: `Fall 2024`,
        },
        workExperience: 'Work Experience',
        cyframe: {
          message: `I worked as a Full Stack Developer at Cyframe, where I played a key role in modernizing and optimizing critical business applications. My responsibilities included refactoring legacy systems, developing internal management tools, and enhancing the user experience through modern UI design. I also led a major migration from Visual SourceSafe (VSS) to TortoiseSVN for patch management, automating workflows and improving deployment traceability across client environments.`,
          ul: {
            l1: `Implemented a full-featured patch management system to replace Visual SourceSafe with TortoiseSVN, including automated tracking of modified files, version control, and client-specific deployment status`,
            l2: `Redesigned the factory production management application "Punch", resulting in significant improvements in system performance and scalability`,
            l3: `Developed a clean, modern user interface that reduced application load times by 30% and improved overall usability for end-users`,
            l4: `Technologies used: ExpressJS, Node.js, OracleDB, Python, SVN (TortoiseSVN), Shell Script, ASP Classic, VBScript, HTML, CSS, JavaScript, jQuery`,
          },
          tech: "Tech stack: ExpressJS, Node.js, OracleDB, Python, SVN (TortoiseSVN), Shell Script, ASP Classic, VBScript, HTML, CSS, JavaScript, jQuery"
        },
        harbor: {
            message: "Harbor.ca is a professional services platform in Canada that connects clients with vetted professionals across various fields. I worked on the backend, database, and domain, helping build a bridge between clients and professionals for business-related projects. My main responsibilities included designing scalable backend architecture, optimizing database performance, and implementing secure authentication and communication flows. I also contributed to business logic for project matching, developed admin tools for service management, and participated in technical decision-making for platform reliability and growth.",
            ul: {
              l1: "Designed and implemented backend architecture with ExpressJS, focusing on scalability and maintainability",
              l2: "Managed database schema, optimized queries, and developed domain logic for efficient service matching",
              l3: "Built secure APIs for user authentication, project sourcing, messaging, and notifications",
              l4: "Developed admin tools for service management, analytics, and reporting",
              l5: "Collaborated with frontend team using React and ChakraUI to deliver seamless user experiences",
              l6: "Participated in technical decision-making to ensure high performance, reliability, and data integrity",
              l7: "Contributed to platform growth by integrating new features and improving deployment workflows"
            },
            tech: "Tech stack: ExpressJS, React, ChakraUI, MongoDB, REST APIs"
        },
        languageAndTech: `Languages and Technologies`,
        stackCarouselTitle: 'My Tech Stack',
        stackCarouselSubtitle: 'Technologies I use most often',
        stackCarouselContinuous: 'Continuous carousel of my tech stack',
        stackCarouselCTA: 'See all my skills',
        stackCarouselTop: 'Top carousel',
        stackCarouselBottom: 'Bottom carousel',
        contactPreviewTitle: 'Contact Preview',
        contactPreviewCTA: 'Get in touch',
        contactPreviewSubtitle: 'Let’s work together or just say hi!',
        cta: 'Contact Me',
        feature1: 'Full Stack Developer',
        feature2: 'Tech Enthusiast',
        portfolioTitle: 'Featured Projects',
        viewProject: 'View Project',
        stackTitle: 'My Tech Stack',
        contactTitle: 'Get in Touch',
        sendMessage: 'Send Message',
        viewSkillsCharts: 'View Skills Charts'
      },
      navbar: {
        home: 'Home',
        cybersecurity: 'Cybersecurity',
        aiEngineering: 'AI Engineering',
        fullStack: 'Full-Stack',
        projects: 'Projects',
        experience: 'Experience',
        flappy: 'Flappy Bird',
        more: 'More',
        skills: 'Skills',
        cv: 'My CV',
        contact: 'Contact',
        menu: 'Menu',
        openMenu: 'Open menu',
        closeMenu: 'Close menu',
        menuTitle: 'Navigation',
        menuSubtitle: 'Select a page',
        brandName: 'Christopher-William',
        brandTagline: 'Cyber x AI x Full-Stack',
        logoAlt: 'Christopher-William logo',
      },
      flappy: {
        instruction: `Press Start Game or Tap to start`,
        play: `Press Space or Tap to jump`,
        start: `Start game`,
        jump: `Jump`,
        pause : `Pause`,
        resume : `Resume game`,
        back: `Back to previous page`,
        restart: `Restart`,
        score: `Score`,
        best: `Best`,
        readyLabel: `Ready`,
        readyTitle: `Ready for launch`,
        readySubtitle: `Tap or press Space to start.`,
        pausedLabel: `Paused`,
        pausedTitle: `Paused`,
        pausedSubtitle: `Resume when you're ready.`,
        gameOverLabel: `Game Over`,
        gameOverTitle: `Run complete`,
        gameOverSubtitle: `Try again to beat your best score.`,
        hint: `Tap/Click or press Space to flap.`,
      },
      error: {
        message : `Oops! The page you're looking for doesn't exist.`,
        home: `Go back to the home page`
      },
      skills: {
        title: `Chart of My Skills`,
        angular: `Capable of building scalable and modular front-end applications using Angular, with experience in routing, reactive forms, and service integration.`,
        cpp: `Strong foundation in C++ for algorithmic problem solving, object-oriented programming, and low-level systems understanding.`,
        chakraui: `Experience using Chakra UI to build accessible, responsive, and customizable React component interfaces with ease.`,
        css3: `Proficient in CSS3 for responsive web design, animations, grid/flex layouts, and modern styling techniques.`,
        dart: `Used Dart alongside Flutter to develop cross-platform mobile applications with efficient code structure and widget management.`,
        expo: `Skilled in building and deploying React Native apps using Expo, including push notifications, asset management, and over-the-air updates.`,
        expressjs: `Experienced in building RESTful APIs and middleware services using Express.js in full-stack JavaScript environments.`,
        firebase: `Used Firebase for real-time databases, authentication, Firestore, and cloud functions in web and mobile applications.`,
        flutter: `Developed cross-platform mobile apps with Flutter using Dart, implementing responsive UIs and Firebase integration.`,
        java: `Strong knowledge of Java for backend systems, object-oriented programming, and enterprise-level application development.`,
        js: `Advanced JavaScript skills with experience across ES6+, DOM manipulation, asynchronous programming, and front-end frameworks.`,
        jwt: `Implemented secure authentication and authorization using JSON Web Tokens (JWT) in multiple web applications.`,
        kotlin: `Used Kotlin for Android development, creating clean and maintainable code with coroutines and Jetpack components.`,
        mongodb: `Worked with MongoDB for flexible NoSQL data modeling, indexing, and aggregation pipelines in scalable backend systems.`,
        aws: `Experience using AWS services such as S3, EC2, Lambda, and SES for hosting, storage, and email infrastructure.`,
        mysql: `Proficient in designing relational schemas, writing complex SQL queries, and managing MySQL databases.`,
        nodejs: `Built backend services and APIs using Node.js, handling asynchronous logic, event-driven architecture, and server-side rendering.`,
        oracle: `Familiar with Oracle Database for enterprise data management, including PL/SQL, stored procedures, and performance tuning.`,
        php: `Experience with PHP for server-side scripting and legacy web applications, including session management and form processing.`,
        postgres: `Knowledge of PostgreSQL for relational database management with advanced querying and JSON support.`,
        python: `Used Python for scripting, automation, and data manipulation with libraries such as Flask, Pandas, and NumPy.`,
        react: `Proficient in developing modern single-page applications using React, with hooks, context API, and Redux for state management.`,
        spring: `Built robust backend services using Spring Boot, integrating JPA, Spring Security, and RESTful APIs.`,
        tailwindcss: `Used Tailwind CSS to rapidly prototype and style web interfaces with utility-first classes and responsive design.`,
        thymeleaf: `Experience using Thymeleaf for server-side rendering of HTML templates in Spring MVC applications.`,
        vuejs: `Developed interactive user interfaces with Vue.js, utilizing Vue Router, Vuex, and component-based architecture.`,
      },      
      footer: {
        copy1: `©`,
        copy2: `All rights reserved.`,
        brand: 'Christopher-William Portfolio',
        tagline: 'Modern, minimalist, and professional UI',
        quickLinks: 'Quick Links',
        social: 'Social',
        legal: 'Legal',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        madeWith: 'Made with Vue.js & Tailwind CSS',
        brandTagline: 'Creating modern solutions with passion.',
        home: 'Home',
        flappy: 'Flappy Bird',
        skills: 'Skills',
        myCV: 'CV',
        contact: 'Contact me',
        info: 'Informations',
        summary: 'Cybersecurity-oriented developer, AI systems engineer, and full-stack builder focused on precise, secure, production-ready systems.',
        motto: 'Engineered for reliability',
        signature: '© {year} Christopher-William — Secure systems · AI orchestration · Full-stack delivery.',
        socialLinks: {
          github: 'GitHub',
          instagram: 'Instagram',
          linkedin: 'LinkedIn',
          facebook: 'Facebook',
        },
      },
      project: {
        title : `My Projects`,
        project: `Go to the project`,
        website: `Go to the website`,
        noWebsite: `Not available`,
        mcgill2023: {
          title: `McGill Hackathon 2023`,
          description: `Designed an interactive space simulation for Hohmann Transfer at McGill Hackathon 2023 using React and p5.js.`,
        },        
        athack2025: {
          title: `CTF {at}Hack 2025`,
          description: `Secured 2nd place at {at}Hack 2025 CTF. Solved web, crypto, forensics, and binary challenges with my ÉTS team.`,
        },        
        flow: {
          title: `Event Flow Planner`,
          description: `Built a custom event planner with React and Spring Boot. Features JWT auth, timeline, and user management.`,
        },
        portefolio: {
          title: `Personal Portfolio`,
          description: `Personal portfolio built with Vue.js, Vue Router, and Tailwind CSS. Showcases my work, skills, and experience.`,
        },
        vocal: {
          title: `Vocal Chat App`,
          description: `Real-time vocal chat app in Kotlin. Peer-to-peer audio streaming, simple UI, and socket-based communication.`,
        }
      },
      cv : {
        title: `My CV`,
        cvLink : "/assets/mordernCV_IT_EN.pdf",
        fileName: "Christopher-William-CV-EN.pdf",
        download: "Download CV (PDF)"
      },
      contact : {
        title: "Contact Me",
        email : "Your Email: ",
        message : "Your Message: ",
        send: "Send message",
          brandName: 'Christopher-William',
          brandTagline: 'Cyber x IA x Full-Stack',
          logoAlt: 'Logo Christopher-William',
        mailholder: "Enter your email here",
        messageholder: "Enter your message here",
      },
      newPortfolio: {
        hero: {
          kicker: 'High-Performance Portfolio',
          title: 'Cybersecurity-Oriented Developer × AI Systems Engineer × Full-Stack Builder',
          subtitle:
            'I engineer secure, intelligent, and production-grade systems with a focus on defensive architecture, resilient AI pipelines, and scalable application delivery.',
          ctaPrimary: 'Explore Projects',
          ctaSecondary: 'Security Deep Dive',
          cards: {
            engineeringTitle: 'Engineering Focus',
            engineeringText: 'Secure-by-design systems and hardening-first delivery.',
            aiTitle: 'AI Focus',
            aiText: 'RAG architecture, orchestration flows, and low-latency inference.',
            platformTitle: 'Platform Focus',
            platformText: 'APIs, data models, Dockerized deployments, and scaling decisions.',
          },
        },
        domains: {
          title: 'Technical Domains',
          subtitle: 'Precision. Performance. Security.',
          cta: 'Open Domain →',
        },
        meta: {
          home: 'Cybersecurity × AI × Full-Stack Engineer',
          cyber: 'Cybersecurity Engineering',
          ai: 'AI Systems Engineering',
          fullStack: 'Full-Stack Engineering',
          projects: 'Technical Projects',
          experience: 'Experience & Education',
          flappy: 'Flappy Bird',
          notFound: 'Page Not Found',
        },
      },
      domains: {
        cyber: {
          title: 'Cybersecurity Engineering',
          summary:
            'Secure-by-design architecture, zero-trust access patterns, threat modeling, and production hardening pipelines.',
          tags: {
            threat: 'Threat Modeling',
            oidc: 'OAuth2/OIDC',
            jwt: 'JWT Rotation',
            siem: 'SIEM',
            pipeline: 'Secure CI/CD',
          },
        },
        ai: {
          title: 'AI Systems Engineering',
          summary:
            'RAG platforms, LLM orchestration, vector indexing, retrieval evaluation, and low-latency inference pathways.',
          tags: {
            rag: 'RAG',
            embeddings: 'Embeddings',
            vector: 'Vector DB',
            llmops: 'LLMOps',
            observability: 'Observability',
          },
        },
        fullStack: {
          title: 'Full-Stack Engineering',
          summary:
            'Backend architecture, API contracts, resilient databases, containerized workloads, and scalable cloud delivery.',
          tags: {
            api: 'Distributed APIs',
            postgres: 'PostgreSQL',
            docker: 'Docker',
            caching: 'Caching',
            deployment: 'Deployment',
          },
        },
      },
      cyberPage: {
        kicker: 'Defensive Engineering',
        title: 'Cybersecurity Engineering',
        subtitle:
          'Security-first architecture built around risk reduction, resilient access controls, observability, and hardening pipelines.',
        capabilitiesTitle: 'Security Capabilities',
        architectureTitle: 'Architecture Blueprint',
        architectureSubtitle: 'Representative security architecture layers and controls:',
        caseTitle: 'Threat Modeling & Hardening Cases',
        references: {
          title: 'Related projects',
          subtitle: 'Read the full technical breakdown in the Projects section.',
          items: {
            athack: '→ AtHack CTF 2025 — writeups & tooling (page Projects)',
          },
        },
        capabilities: [
          'Threat modeling with STRIDE and abuse-case mapping',
          'Identity hardening using OAuth2 + OIDC + rotating JWT refresh tokens',
          'Data-at-rest and data-in-transit encryption with managed key lifecycles',
          'Secure pipeline controls: SAST, dependency scanning, policy checks, signed artifacts',
          'SIEM telemetry with actionable detection rules and incident playbooks',
        ],
        architecture: {
          identity: {
            label: 'Identity Layer',
            value: 'MFA, conditional access, short-lived tokens, role scoping',
          },
          api: {
            label: 'API Protection',
            value: 'Rate limits, schema validation, authz middleware, audit trails',
          },
          data: {
            label: 'Data Protection',
            value: 'Field-level encryption, secrets vaulting, immutable backups',
          },
          runtime: {
            label: 'Runtime Defense',
            value: 'Container scanning, runtime policies, anomaly alerts',
          },
        },
        cases: [
          {
            title: 'AtHack CTF 2025 — Defensive & Offensive Practice',
            problem: 'Solve real-world security challenges across web, crypto, forensics, and binary domains.',
            approach:
              'Applied exploit chains, reverse engineering, and forensics workflows with strict time constraints.',
            outcome: 'Secured 2nd place and documented the full solution set with the ÉTS team.',
          },
          {
            title: 'Secure Pipeline for Multi-Service Platform',
            problem: 'Inconsistent dependency hygiene and no centralized release policy enforcement.',
            approach:
              'Introduced policy-as-code gates, vulnerability budgets, SBOM generation, and signed container promotion.',
            outcome: 'Cut critical exposure lead-time from days to hours and standardized release compliance.',
          },
        ],
      },
      aiPage: {
        kicker: 'Intelligence Infrastructure',
        title: 'AI Systems Engineering',
        subtitle:
          'End-to-end AI pipeline engineering focused on grounded outputs, traceable behavior, and predictable performance.',
        architectureTitle: 'AI Agent Architecture',
        architectureSubtitle: 'Technical flow from ingestion to evaluation:',
        performanceTitle: 'Performance Optimization',
        labsTitle: 'Lab Systems',
        labels: {
          system: 'System',
          challenge: 'Challenge',
          result: 'Result',
        },
        blocks: {
          ingestion: { name: 'Ingestion', detail: 'Document parsers, chunking strategy, metadata normalization' },
          embedding: { name: 'Embedding Layer', detail: 'Batch embedding pipelines with dimension/version governance' },
          retrieval: { name: 'Retrieval', detail: 'Hybrid search (semantic + lexical), rerankers, and filter controls' },
          orchestration: { name: 'Orchestration', detail: 'Agent workflow graph, tool calling, guardrails, and retries' },
          generation: { name: 'Generation', detail: 'Prompt templates, structured outputs, citation checks, fallback models' },
          evaluation: { name: 'Evaluation', detail: 'Latency/quality dashboards, hallucination checks, and drift detection' },
        },
        performance: [
          'Token budget optimization with prompt compaction and context windows by query class',
          'Caching layers for embeddings and retrieval candidates to control p95 latency',
          'Parallel retrieval + tool execution for orchestration throughput gains',
          'Asynchronous queueing for heavy indexing and evaluation workloads',
        ],
        labs: [
          {
            title: 'Support Agent with RAG + Tooling',
            system: 'Knowledge index over product docs, embeddings in vector DB, policy-bound function calls.',
            challenge: 'Balancing response speed with citation reliability across heterogeneous content.',
            result: 'Improved grounded-answer rate and cut median response latency by 34%.',
          },
          {
            title: 'Ops Intelligence Copilot',
            system: 'Event stream summarization, anomaly explanation, and runbook recommendation pipeline.',
            challenge: 'Maintaining deterministic output format under variable incident load.',
            result: 'Reduced triage time and increased runbook adoption for on-call engineers.',
          },
        ],
        references: {
          title: 'Related projects',
          subtitle: 'Jump to the detailed builds and outcomes in the Projects section.',
          items: {
            louis: '→ Louis AI (page Projects)',
            cyframeRag: '→ CyFrame RAG (page Projects)',
            cyframeDev: '→ CyFrame AI Dev (page Projects)',
            evalHarness: '→ R&D Eval Harness (page Projects)',
            harborBlog: '→ Harbor Blog RAG (page Projects)',
          },
        },
      },
      aiProjects: {
        title: 'AI Systems Portfolio',
        badge: 'Orchestration-first',
        subtitle:
          'Systems that combine retrieval, decision logic, structured extraction, and pricing for production-grade autonomy.',
        emphasisTitle: 'Core principle:',
        emphasisText: 'This is not a chatbot — it is an orchestration engine for real workflows.',
        labels: {
          capabilities: 'Capabilities',
          stack: 'Technical stack',
          emphasis: 'Emphasis',
        },
        louis: {
          title: 'Louis AI — Autonomous AI Agent for Harbor',
          summary:
            'Stateful AI orchestration engine for a service-matching marketplace with demand-first conversational flow and structured data extraction.',
          capabilities: [
            'Demand-first conversational AI and multi-phase orchestration',
            'Structured extraction with field-level option mapping',
            'Semantic service matching using embeddings',
            'Fallback logic and hallucination prevention',
            'Coin-based pricing algorithm integration',
            'Streaming SSE response architecture',
            'Persistent MongoDB session state',
            'Performance tuning for low latency',
            'Persona alignment for psychological UX',
            'Embeddings pipeline with BGE-M3 + OpenAI',
            'Dockerized deployment on Render',
          ],
          emphasis: 'Not a chatbot. A full orchestration engine with retrieval, decisions, extraction, and pricing.',
        },
        cyframeRag: {
          title: 'CyFrame AI Assistant — Enterprise RAG System',
          summary:
            'AI assistant integrated into a legacy ASP + IIS ERP with secure multilingual documentation retrieval.',
          capabilities: [
            'Python indexing pipeline with SVN ingestion',
            'Persistent vector storage and secure internal access',
            'Multilingual search (FR/EN/ES)',
            'Decoupled AI backend integrated into legacy frontend',
            'Production-ready retrieval stack',
            'Enterprise-safe deployment posture',
          ],
          emphasis: 'Legacy modernization via AI while keeping enterprise security boundaries intact.',
        },
        cyframeDev: {
          title: 'CyFrame AI Developer Chatbot',
          summary:
            'Developer-focused assistant for code and documentation retrieval with context-aware technical Q&A.',
          capabilities: [
            'Internal code + documentation retrieval',
            'Context-aware technical workflows and Q&A',
            'Planned integration with internal repositories',
            'Developer productivity acceleration',
          ],
          emphasis: 'AI augmentation for engineering teams: faster debugging and onboarding.',
        },
        evalHarness: {
          title: 'R&D AI Evaluation Harness',
          summary:
            'Independent benchmarking module for RAG systems with systematic evaluation methodology.',
          capabilities: [
            "Recall{'@'}K measurement and latency benchmarking",
            'Comparison of BM25, vector, hybrid, and reranking',
            'Baseline experimentation framework',
            'Scientific evaluation and reproducibility',
          ],
          emphasis: 'Data-driven AI engineering with measurable retrieval performance.',
        },
      },
      fullStackPage: {
        kicker: 'Production Engineering',
        title: 'Full-Stack Engineering',
        subtitle:
          'Full lifecycle delivery from architecture and API contracts to container deployment, observability, and scaling.',
        productionTitle: 'Production Deployment Decisions',
        labels: {
          decision: 'Decision',
          impact: 'Impact',
        },
        pillars: {
          backend: {
            title: 'Backend Architecture',
            details: 'Service boundaries, async workflows, event-driven integration, and fault isolation patterns.',
          },
          api: {
            title: 'API Design',
            details: 'Typed contracts, idempotent endpoints, pagination strategies, and versioned evolution plans.',
          },
          data: {
            title: 'Data Layer',
            details: 'Relational + NoSQL selection, indexing strategy, transaction safety, and migration discipline.',
          },
          devops: {
            title: 'DevOps + Delivery',
            details: 'Dockerized services, CI/CD release orchestration, quality gates, and progressive deployment.',
          },
          scaling: {
            title: 'Scalability',
            details: 'Horizontal scaling, cache strategy, queue backpressure, and production observability.',
          },
        },
        production: [
          {
            title: 'High-Traffic API Platform',
            decision: 'Introduced read-through caching and async job queues for expensive operations.',
            impact: 'Stabilized p95 response time under load spikes and reduced database pressure.',
          },
          {
            title: 'Containerized Delivery Workflow',
            decision: 'Standardized multi-stage Docker builds with security scanning and deployment promotion.',
            impact: 'Accelerated release velocity while strengthening runtime consistency.',
          },
        ],
      },
      projectsPage: {
        kicker: 'Engineering Portfolio',
        title: 'Projects With Technical Depth',
        subtitle:
          'Each project highlights the original problem, architecture decisions, stack selection, technical constraints, and measurable outcomes.',
        labels: {
          problem: 'Problem',
          approach: 'Approach',
          architecture: 'Architecture',
          stack: 'Stack',
          outcome: 'Outcome',
          challenges: 'Technical Challenges',
          visuals: 'Visuals',
          related: 'Related',
          repository: 'Repository',
          demo: 'Live Demo',
        },
        flappy: {
          title: 'Bonus: Flappy Bird',
          subtitle: 'Take a quick break and play the mini-game, then return to the portfolio.',
          play: 'Play Flappy Bird',
          back: 'Back to portfolio',
        },
      },
      projectsDetailed: {
        categories: {
          cyber: 'Cybersecurity',
          ai: 'AI Engineering',
          fullStack: 'Full-Stack',
          research: 'R&D',
          infrastructure: 'Infrastructure',
        },
        references: {
          aiPage: '→ AI Engineering page',
          cyberPage: '→ Cybersecurity page',
          cyframeExp: '→ CyFrame (page Experience)',
          harborExp: '→ Harbor (page Experience)',
        },
        athack: {
          name: 'AtHack CTF 2025',
          problem: 'Compete in a real-world CTF with offensive/defensive challenges under time pressure.',
          architecture: 'Challenge reconnaissance, exploit chains, and incident-style writeups across multiple domains.',
          challenges: [
            'Web exploitation under time pressure',
            'Crypto/forensics pivoting with limited telemetry',
            'Binary reversing with incomplete context',
          ],
          outcome: 'Secured 2nd place with the ÉTS team and a complete writeup set.',
          visuals: 'CTF scoreboard + challenge notes',
        },
        mcgill: {
          name: 'McGill Hackathon 2023 — Hohmann Transfer Simulation',
          problem: 'Explain orbital transfer mechanics with an interactive, real-time simulation.',
          architecture: 'React UI + p5.js simulation layer + physics parameter controls.',
          challenges: [
            'Calibrating simulation stability with live input',
            'Presenting complex physics in a clear UI',
          ],
          outcome: 'Delivered a visual, educational experience for orbital mechanics.',
          visuals: 'Simulation UI + trajectory visualization',
        },
        flow: {
          name: 'Event Flow Planner',
          problem: 'Plan multi-step events with authentication and timeline orchestration.',
          architecture: 'React frontend + Spring Boot API + JWT auth + relational storage.',
          challenges: [
            'Designing secure role-based access patterns',
            'Keeping timeline data consistent under edits',
          ],
          outcome: 'Functional planning platform with secure user flows.',
          visuals: 'Timeline UI + dashboard screens',
        },
        portfolio: {
          name: 'Portfolio Engineering Platform',
          problem: 'Create a high-performance, technically rich portfolio that reflects systems-level engineering depth.',
          architecture:
            'Component-driven frontend + structured content model + route-level separation of engineering domains + optimized delivery.',
          challenges: [
            'Balancing rich technical narrative with clarity and scanning speed',
            'Designing reusable layouts for deep engineering sections',
            'Keeping visual identity futuristic yet professional and readable',
          ],
          outcome:
            'Deployed a modern portfolio with clear positioning across cybersecurity, AI systems, and full-stack engineering.',
          visuals: 'Page-level layout wireframes + UI screenshots',
        },
        vocal: {
          name: 'Vocal Chat App',
          problem: 'Enable real-time voice communication over a lightweight client.',
          architecture: 'Kotlin client + socket-based audio streaming pipeline.',
          challenges: [
            'Maintaining low-latency audio streaming',
            'Handling unreliable connections gracefully',
          ],
          outcome: 'Functional real-time voice chat prototype.',
          visuals: 'App UI + connection flow notes',
        },
        louis: {
          name: 'Louis AI — Autonomous Orchestration Engine',
          problem: 'Match user demand to services with structured extraction and pricing logic.',
          architecture: 'Stateful orchestration + embeddings + extraction + pricing + SSE streaming.',
          challenges: [
            'Avoiding hallucinations in structured outputs',
            'Maintaining low latency with multi-phase orchestration',
            'Ensuring consistent state across sessions',
          ],
          outcome: 'Delivered an orchestration-first AI agent for a service marketplace.',
          visuals: 'Agent flow diagram + orchestration states',
        },
        harborBlog: {
          name: 'Harbor Blog RAG System',
          problem:
            'Support informational questions (blog + platform) without triggering the service-matching pipeline.',
          architecture:
            'Master intent → isRagWorthy flag → RAG branch → semantic search → LLM synthesis → source attribution.',
          challenges: [
            'Separating transactional vs informational intents',
            'Tuning chunking, overlap, and confidence thresholds',
            'Building reindexing and cost controls for production',
          ],
          outcome:
            'Production-grade informational RAG with safe fallbacks and source attribution.',
          visuals: 'Intent-routing diagram + RAG flow',
        },
        svnManager: {
          name: 'SVNManager — Legacy Patch System Modernization',
          problem: 'Replace a legacy internal tool (PatchHelper) with a maintainable, modern workflow.',
          architecture:
            'Modern patch management workflow with improved repo handling and deployment reliability.',
          challenges: [
            'Migrating away from aging infrastructure without downtime',
            'Reducing friction for developers and release managers',
            'Designing a cleaner, maintainable architecture',
          ],
          outcome:
            'Core infrastructure modernization that enabled scalable AI integration later.',
          visuals: 'Workflow migration map + deployment pipeline overview',
        },
        cyframeRag: {
          name: 'CyFrame Enterprise RAG Assistant',
          problem: 'Search multilingual documentation inside a legacy ERP system safely.',
          architecture: 'Python ingestion + vector DB + decoupled AI backend + IIS/ASP integration.',
          challenges: [
            'Secure access boundaries for internal documents',
            'Reliable retrieval in FR/EN/ES',
          ],
          outcome: 'Enterprise-safe RAG integration for internal productivity.',
          visuals: 'RAG pipeline + internal UI integration',
        },
        cyframeDev: {
          name: 'CyFrame AI Developer Assistant',
          problem: 'Accelerate developer debugging and documentation lookup.',
          architecture: 'Code/document retrieval layer + context-aware Q&A.',
          challenges: [
            'Maintaining accurate context for technical queries',
            'Integrating multiple internal knowledge sources',
          ],
          outcome: 'Internal AI assistant to improve engineering velocity.',
          visuals: 'Developer workflow diagram',
        },
        evalHarness: {
          name: 'R&D AI Evaluation Harness',
          problem: 'Benchmark RAG strategies with measurable retrieval performance.',
          architecture: 'Evaluation harness comparing BM25, vector, hybrid, and reranking.',
          challenges: [
            'Building fair, reproducible comparisons',
            "Capturing latency and Recall{'@'}K tradeoffs",
          ],
          outcome: 'Data-driven evaluation platform for AI retrieval choices.',
          visuals: 'Evaluation dashboards + experiment matrix',
        },
      },
      theme: {
        dark: 'Dark',
        light: 'Light',
      },
      language: {
        toggle: 'Switch language',
      },
      experiencePage: {
        kicker: 'Career & Education',
        title: 'Experience & Education',
        subtitle: 'Professional experience at CyFrame and Harbor, plus formal studies and specialization.',
        workTitle: 'Work Experience',
        educationTitle: 'Education',
        referencesLabel: 'References',
        stacks: {
          title: 'Stacks I use and know',
          subtitle: 'Tools and technologies applied across backend, frontend, and platform work.',
          items: {
            node: 'Node.js',
            express: 'Express.js',
            react: 'React',
            vue: 'Vue.js',
            python: 'Python',
            java: 'Java',
            spring: 'Spring Boot',
            mongodb: 'MongoDB',
            mysql: 'MySQL',
            oracle: 'OracleDB',
            aws: 'AWS',
            tailwind: 'Tailwind CSS',
            chakra: 'Chakra UI',
          },
        },
        work: {
          cyframe: {
            company: 'CyFrame',
            role: 'Full-Stack Developer',
            timeline: '2023',
            summary:
              'Modernized legacy applications, delivered internal management tools, and led patch-management migration workflows.',
            highlights: {
              0: 'Migrated patch management from VSS to TortoiseSVN with automated tracking.',
              1: 'Redesigned the Punch production system for performance and usability.',
              2: 'Built a modern UI reducing load time by 30%.',
            },
            stack: 'Stack: ExpressJS, Node.js, OracleDB, Python, SVN, Shell, ASP Classic',
          },
          harbor: {
            company: 'Harbor.ca',
            role: 'Backend & Platform Engineering',
            timeline: '2024',
            summary:
              'Built scalable backend architecture, optimized data workflows, and delivered secure APIs for a services marketplace.',
            highlights: {
              0: 'Designed core backend services and domain logic for service matching.',
              1: 'Implemented secure authentication, messaging, and notification APIs.',
              2: 'Collaborated on admin tools and analytics for platform operations.',
            },
            stack: 'Stack: ExpressJS, React, ChakraUI, MongoDB, REST APIs',
          },
        },
        education: {
          andre: {
            school: 'Cégep André-Laurendeau',
            program: 'Computer Science Technology (Back-End & Full-Stack)',
            summary:
              'Hands-on training across full-stack development, backend systems, and secure application architecture.',
            highlights: {
              0: 'Angular/React/Thymeleaf web development and Flutter mobile apps.',
              1: 'Spring Boot, Python, Kotlin, JWT auth, MySQL/H2 databases.',
              2: 'Capstone projects delivering production-style web apps.',
            },
            date: 'Graduated: May 2024',
          },
          ets: {
            school: 'ÉTS — École de technologie supérieure',
            program: 'B.Eng. Software Engineering',
            summary:
              'Focused on advanced software design, AI systems, cybersecurity, and cloud computing.',
            highlights: {
              0: 'Advanced software architecture and distributed systems.',
              1: 'Applied AI and cybersecurity specialization.',
              2: 'Cloud computing and systems reliability engineering.',
            },
            date: 'Started: Fall 2024',
          },
        },
        references: {
          aiDev: '→ CyFrame AI Developer (AI page)',
          aiHarness: '→ R&D AI Eval Harness (AI page)',
          aiRag: '→ CyFrame RAG Assistant (AI page)',
          svnManager: '→ SVNManager (Projects page)',
          louis: '→ Louis AI (AI page)',
          harborBlog: '→ Harbor Blog RAG (Projects page)',
        },
        cvButton: 'Download My CV',
      },
    },
    fr: {
      HomeComponent: {
        welcome: 'Bienvenue dans mon application!',
        heroTagline: "Je suis Christopher-William",
        about: 'À propos de moi',
        aboutMeMessage: `Je m'appelle Christopher-William et je suis passionné par la technologie et l'électronique depuis l'âge de 12 ans. Très tôt, j'ai commencé à démonter, comprendre, puis concevoir mes propres circuits électroniques, ce qui m’a permis de développer une compréhension intuitive du fonctionnement des appareils qui nous entourent. Cette curiosité m’a naturellement conduit vers le monde du développement logiciel.
                          Aujourd’hui âgé de {age} ans, je poursuis des études en génie logiciel, où je combine mes compétences en programmation, en algorithmique, en architecture logicielle et en ingénierie des systèmes. Mon parcours a débuté avec Java, en développant des serveurs Minecraft personnalisés ainsi que des mods, ce qui m’a permis d’apprendre les bases de la programmation orientée objet, de la gestion réseau, et de l’interaction entre client et serveur dans un environnement réel utilisé par des centaines d’utilisateurs.
                          Depuis, je n’ai cessé de relever de nouveaux défis et d’élargir mon champ de compétences. J’ai touché à de nombreuses technologies, allant du développement web (frontend et backend), à l’embarqué, en passant par les bases de données, les API REST, le DevOps, et l’intelligence artificielle. J’aime particulièrement concevoir des solutions robustes, efficaces et élégantes, que ce soit en solo ou au sein d’une équipe de développement.
                          Ma motivation repose sur la soif constante d’apprendre, de résoudre des problèmes concrets, et de créer des projets à fort impact. Que ce soit en contribuant à des projets open source, en développant des outils pour automatiser des tâches ou en expérimentant avec de nouveaux langages ou frameworks, je suis toujours prêt à explorer de nouveaux horizons.`,
        myAmbitions: 'Mes ambitions',
        myAmbitionsMessage: `Je vise à contribuer à des projets significatifs et à fort impact dans le domaine de l’informatique, qu’il s’agisse de développer des solutions logicielles innovantes, d’optimiser des systèmes complexes ou de répondre à des problématiques concrètes par la technologie. Mon objectif est non seulement de mettre en œuvre mes compétences actuelles avec efficacité, mais aussi de les enrichir continuellement en apprenant de nouveaux outils, langages et paradigmes. Je suis animé par une volonté constante de m’améliorer, de rester à l’affût des évolutions du secteur, et de saisir chaque occasion pour progresser en tant que développeur, ingénieur et résolveur de problèmes.`,
        studies: 'Études',
        andreLaurendeau: {
          message: `J'ai complété une formation en Techniques de l'informatique au Cégep André-Laurendeau, avec une spécialisation en développement back-end, tout en élargissant mes compétences vers le développement full-stack. Cette formation m’a permis d’acquérir une expertise pratique dans l’utilisation de technologies modernes, autant côté client que côté serveur, et de consolider ma compréhension de l’architecture logicielle, des algorithmes avancés et de la gestion efficace des bases de données.`,
          ul: {
            program: 'Programme : Techniques de l’informatique',
            specialization: 'Spécialisation : Développement Back-End et Full-Stack',
            keyCourses: `Cours clés : Développement web avec Angular, React et Thymeleaf ; Développement mobile avec Flutter ; Programmation serveur avec Spring Boot, Python et Kotlin ; Sécurité et authentification avec JWT ; Gestion de bases de données avec MySQL et H2 ; Services infonuagiques avec Firebase`,
            projects: `Projets notables : Application web de gestion du temps et refonte du système de sélection de stages du cégep, développées avec Spring Boot, React et MySQL ; Application mobile multiplateforme conçue avec Flutter et Firebase ; Système d’authentification sécurisé basé sur JWT ; Interfaces dynamiques avec Angular et intégration d’API REST sécurisées.`,
          },
          gradMessage: 'Date de graduation :',
          gradDate: ' Mai 2024',
        },
        ets: {
          message: `Actuellement inscrit au Baccalauréat en génie logiciel à l'ÉTS (École de technologie supérieure), je poursuis une formation centrée sur la conception logicielle avancée, l’architecture de systèmes et l’optimisation algorithmique. Mon objectif est d’approfondir mes connaissances en intelligence artificielle et en cybersécurité, deux domaines qui me passionnent et qui sont au cœur des enjeux technologiques actuels. Le programme me permet de développer des compétences solides en ingénierie logicielle tout en explorant des sujets de pointe liés à la sécurité des systèmes, à l’apprentissage automatique et à l’infonuagique.`,
          ul: {
            program: `Programme : Baccalauréat en génie logiciel`,
            keyCourses: `Domaines clés : Intelligence artificielle, Cybersécurité, Conception logicielle avancée, Informatique en nuage, Systèmes distribués`,
            projects: `Projets à venir : Développement d’un outil de gestion de tâches sécurisé basé sur le cloud, intégrant des fonctionnalités collaboratives et une couche de protection des données utilisateurs.`,
          },
          gradMessage: 'Date de début :',
          gradDate: ` Automne 2024`,
        },
        workExperience: 'Expérience professionnelle',
        cyframe: {
          message: `J'ai travaillé en tant que développeur Full Stack chez Cyframe, où j'ai contribué à la modernisation et à l’optimisation d'applications critiques. Mon rôle principal s’est concentré sur la refonte de systèmes existants, le développement d’outils de gestion interne et l'amélioration de l'expérience utilisateur via des interfaces modernes. J’ai également mené une transition majeure du système de gestion de patchs de Visual SourceSafe (VSS) vers TortoiseSVN, en automatisant les processus et en assurant une meilleure traçabilité des déploiements.`,
          ul: {
            l1: `Mise en place d’un nouveau système complet de gestion des correctifs (patchs), remplaçant Visual SouceSafe par TortoiseSVN, incluant le suivi automatisé des fichiers modifiés, des versions, et des environnements clients où les correctifs sont installés`,
            l2: `Refonte complète de l'application de gestion de production de l'usine "Punch", avec une amélioration significative des performances et de la scalabilité`,
            l3: `Conception d’une interface utilisateur moderne et épurée, réduisant les temps de chargement de 30% et facilitant la navigation pour les utilisateurs finaux`,
            l4: `Technologies utilisées : ExpressJS, Node.js, OracleDB, Python, SVN (TortoiseSVN), Shell Script, ASP Classic, VB Script, HTML, CSS, JavaScript, jQuery`,
          },
          tech: "Stack technique : ExpressJS, Node.js, OracleDB, Python, SVN (TortoiseSVN), Shell Script, ASP Classic, VB Script, HTML, CSS, JavaScript, jQuery"
        },
        harbor: {
            message: "Harbor.ca est une plateforme canadienne de services professionnels qui met en relation des clients avec des experts qualifiés dans divers domaines. J'ai travaillé sur le backend, la base de données et le domaine métier, contribuant à créer un pont entre les clients et les professionnels pour des projets d'affaires. Mes principales responsabilités incluaient la conception d'une architecture backend évolutive, l'optimisation des performances de la base de données, et la mise en place de flux sécurisés d'authentification et de communication. J'ai également participé à la logique métier pour l'appariement des projets, au développement d'outils d'administration pour la gestion des services, et aux décisions techniques pour assurer la fiabilité et la croissance de la plateforme.",
            ul: {
              l1: "Conception et implémentation d'une architecture backend évolutive avec ExpressJS, axée sur la maintenabilité",
              l2: "Gestion du schéma de base de données, optimisation des requêtes et développement de la logique métier pour un appariement efficace des services",
              l3: "Développement d'API sécurisées pour l'authentification, la recherche de projets, la messagerie et les notifications",
              l4: "Création d'outils d'administration pour la gestion des services, l'analyse et le reporting",
              l5: "Collaboration avec l'équipe frontend utilisant React et ChakraUI pour offrir une expérience utilisateur fluide",
              l6: "Participation aux décisions techniques pour garantir la performance, la fiabilité et l'intégrité des données",
              l7: "Contribution à la croissance de la plateforme par l'intégration de nouvelles fonctionnalités et l'amélioration des workflows de déploiement"
            },
            tech: "Stack technique : ExpressJS, React, ChakraUI, MongoDB, APIs REST"
        },
        languageAndTech: `Langues et technologies`,
        stackCarouselTitle: 'Ma stack technique',
        stackCarouselSubtitle: 'Technologies que j’utilise le plus souvent',
        stackCarouselContinuous: 'Carrousel continu de ma stack technique',
        stackCarouselCTA: 'Voir toutes mes compétences',
        stackCarouselTop: 'Carrousel du haut',
        stackCarouselBottom: 'Carrousel du bas',
        contactPreviewTitle: 'Aperçu contact',
        contactPreviewCTA: 'Entrer en contact',
        contactPreviewSubtitle: 'Collaborons ou dites simplement bonjour !',
        cta: 'Contactez-moi',
        feature1: 'Développeur Full Stack',
        feature2: 'Passionné de Tech',
        portfolioTitle: 'Projets phares',
        viewProject: 'Voir le projet',
        stackTitle: 'Ma Stack Technique',
        contactTitle: 'Me contacter',
        sendMessage: 'Envoyer le message',
        viewSkillsCharts: 'Voir les graphiques de compétences'
      },
      navbar: {
        home: 'Accueil',
        cybersecurity: 'Cybersécurité',
        aiEngineering: 'Ingénierie IA',
        fullStack: 'Full-Stack',
        projects: 'Projets',
        experience: 'Expérience',
        flappy: 'Flappy Bird',
        more: 'Plus',
        skills: 'Compétences',
        cv: 'Mon CV',
        contact: 'Contact',
        menu: 'Menu',
        openMenu: 'Ouvrir le menu',
        closeMenu: 'Fermer le menu',
        menuTitle: 'Navigation',
        menuSubtitle: 'Sélectionnez une page',
        brandName: 'Christopher-William',
        brandTagline: 'Cyber x IA x Full-Stack',
        logoAlt: 'Logo Christopher-William',
      },
      flappy : {
        instruction: `Appuyez sur Démarrer le jeu ou tapez pour commencer`,
        play: `Appuyez sur Espace ou touchez pour sauter`,
        start: `Démarrer le jeu`,
        jump: `Sauter`,
        pause : `Pause`,
        resume : `Reprendre le jeu`,
        back: `Retour à la page précédente`,
        restart: `Rejouer`,
        score: `Score`,
        best: `Meilleur`,
        readyLabel: `Prêt`,
        readyTitle: `Prêt au lancement`,
        readySubtitle: `Touchez ou appuyez sur Espace pour démarrer.`,
        pausedLabel: `Pause`,
        pausedTitle: `Pause`,
        pausedSubtitle: `Reprenez quand vous êtes prêt.`,
        gameOverLabel: `Terminé`,
        gameOverTitle: `Fin de partie`,
        gameOverSubtitle: `Rejouez pour battre votre meilleur score.`,
        hint: `Touchez/Cliquez ou appuyez sur Espace pour battre des ailes.`,
      },
      error: {
        message : `Oups! La page que vous recherchez n'existe pas.`,
        home: `Retourner à la page d'accueil`
      },
      skills: {
        title: `Graphique de mes compétences`,
        angular: `Capable de développer des applications front-end modulaires et évolutives avec Angular, avec expérience en routing, formulaires réactifs et intégration de services.`,
        cpp: `Solide base en C++ pour la résolution de problèmes algorithmiques, la programmation orientée objet et la compréhension des systèmes bas niveau.`,
        chakraui: `Expérience avec Chakra UI pour créer des interfaces React accessibles, responsives et facilement personnalisables.`,
        css3: `Bonne maîtrise de CSS3 pour le design responsive, les animations, les grilles/flexbox et les techniques de stylisation modernes.`,
        dart: `Utilisation de Dart avec Flutter pour développer des applications mobiles multiplateformes avec une structure de code efficace et une gestion de widgets optimale.`,
        expo: `Compétent dans le développement et le déploiement d'applications React Native via Expo, incluant les notifications push, la gestion des assets et les mises à jour OTA.`,
        expressjs: `Expérience dans la création d'API RESTful et de middlewares avec Express.js dans des environnements JavaScript full-stack.`,
        firebase: `Utilisation de Firebase pour les bases de données en temps réel, l'authentification, Firestore et les fonctions cloud dans des applications web et mobiles.`,
        flutter: `Développement d’applications mobiles multiplateformes avec Flutter, intégrant des interfaces réactives et Firebase.`,
        java: `Solide maîtrise de Java pour le développement backend, la programmation orientée objet et les applications d'entreprise.`,
        js: `Excellente connaissance de JavaScript (ES6+), manipulation du DOM, programmation asynchrone et utilisation de frameworks modernes.`,
        jwt: `Implémentation de systèmes d'authentification et d'autorisation sécurisés via JSON Web Tokens (JWT) dans plusieurs applications web.`,
        kotlin: `Utilisation de Kotlin pour le développement Android, avec du code propre et maintenable grâce aux coroutines et aux composants Jetpack.`,
        mongodb: `Expérience avec MongoDB pour la modélisation NoSQL flexible, l’indexation et les pipelines d’agrégation dans des systèmes backend évolutifs.`,
        aws: `Utilisation de services AWS comme S3, EC2, Lambda et SES pour l’hébergement, le stockage et l’envoi d’emails.`,
        mysql: `Compétence dans la conception de schémas relationnels, l’écriture de requêtes SQL complexes et la gestion de bases de données MySQL.`,
        nodejs: `Développement de services backend et d’API avec Node.js, en gérant la logique asynchrone, l’architecture événementielle et le rendu côté serveur.`,
        oracle: `Familiarité avec Oracle Database pour la gestion de données à l’échelle entreprise, incluant PL/SQL, procédures stockées et optimisation.`,
        php: `Expérience avec PHP pour le développement côté serveur et d’applications web legacy, incluant la gestion de sessions et le traitement de formulaires.`,
        postgres: `Bonne connaissance de PostgreSQL pour la gestion de bases relationnelles avec des requêtes avancées et le support JSON.`,
        python: `Utilisation de Python pour le scripting, l’automatisation et la manipulation de données avec Flask, Pandas, NumPy, etc.`,
        react: `Maîtrise du développement d’applications monopages modernes avec React, utilisation des hooks, du Context API et de Redux pour la gestion d’état.`,
        spring: `Développement de services backend robustes avec Spring Boot, intégrant JPA, Spring Security et des API RESTful.`,
        tailwindcss: `Utilisation de Tailwind CSS pour prototyper et styliser rapidement des interfaces web avec une approche utilitaire responsive.`,
        thymeleaf: `Expérience avec Thymeleaf pour le rendu côté serveur de templates HTML dans des applications Spring MVC.`,
        vuejs: `Développement d'interfaces utilisateur interactives avec Vue.js, en utilisant Vue Router, Vuex et une architecture basée sur les composants.`,
        asp: `Travail avec des applications legacy en ASP Classic, comprenant la logique VBScript, ADO et le rendu serveur traditionnel.`
      },
      footer: {
        copy1: `©`,
        copy2: `Tous droits réservés.`,
        brand: 'Portfolio Christopher-William',
        tagline: 'UI moderne, minimaliste et professionnelle',
        quickLinks: 'Liens rapides',
        social: 'Réseaux sociaux',
        legal: 'Mentions légales',
        privacy: 'Politique de confidentialité',
        terms: 'Conditions d’utilisation',
        madeWith: 'Réalisé avec Vue.js & Tailwind CSS',
        brandTagline: 'Créer des solutions modernes avec passion.',
        home: "Accueil",
        flappy: "Flappy Bird",
        skills: "Compétences",
        myCV: "CV",
        contact: "Contactez-moi",
        info: "Informations",
        summary: 'Développeur orienté cybersécurité, ingénieur systèmes IA et full-stack axé sur des systèmes précis, sûrs et prêts pour la production.',
        motto: 'Conçu pour la fiabilité',
        signature: '© {year} Christopher-William — Systèmes sécurisés · Orchestration IA · Livraison full-stack.',
        socialLinks: {
          github: 'GitHub',
          instagram: 'Instagram',
          linkedin: 'LinkedIn',
          facebook: 'Facebook',
        },
      },
      project :{
        title : `Mes Projets`,
        project: `Aller au projet`,
        website: `Aller au site web`,
        noWebsite: `Non-disponible`,
        mcgill2023: {
          title: `Hackathon McGill 2023`,
          description: `Simulation spatiale interactive pour le transfert de Hohmann au Hackathon McGill 2023 avec React et p5.js.`,
        },
        athack2025: {
          title: `CTF {at}Hack 2025`,
          description: `2ᵉ place au CTF {at}Hack 2025. Résolution de défis web, crypto, forensics et binaires avec l'équipe ÉTS.`,
        },        
        flow: {
          title: `Event Flow Planner`,
          description: `Planificateur d’événements sur mesure avec React et Spring Boot. Authentification JWT, timeline et gestion utilisateurs.`,
        },
        portefolio: {
          title: `Portfolio personnel`,
          description: `Portfolio personnel avec Vue.js, Vue Router et Tailwind CSS. Présente mes projets, compétences et parcours.`,
        },
        vocal: {
          title: `Application de chat vocal`,
          description: `Application de chat vocal en temps réel en Kotlin. Streaming audio, UI simple et communication par sockets.`,
        }
      },
      cv : {
        title: `Mon CV`,
        cvLink : "/assets/mordernCV_IT_FR.pdf",
        fileName: "Christopher-William-CV-FR.pdf",
        download: "Télécharger le CV (PDF)"
      },
      contact : {
        title: "Contactez-moi",
        email : "Votre Email: ",
        message : "Votre Message: ",
        send: "Envoyer le message",
        mailholder: "Entrez votre email ici",
        messageholder: "Entrez votre message ici",
      },
      newPortfolio: {
        hero: {
          kicker: 'Portfolio haute performance',
          title: 'Développeur orienté cybersécurité × Ingénieur systèmes IA × Full-Stack',
          subtitle:
            'Je conçois des systèmes sécurisés, intelligents et prêts pour la production, avec une approche défensive, des pipelines IA résilients et une livraison scalable.',
          ctaPrimary: 'Explorer les projets',
          ctaSecondary: 'Analyse sécurité',
          cards: {
            engineeringTitle: 'Focus ingénierie',
            engineeringText: 'Systèmes secure-by-design et livraison orientée durcissement.',
            aiTitle: 'Focus IA',
            aiText: 'Architecture RAG, orchestration et inférence faible latence.',
            platformTitle: 'Focus plateforme',
            platformText: 'API, modèles de données, déploiements Docker et décisions de scalabilité.',
          },
        },
        domains: {
          title: 'Domaines techniques',
          subtitle: 'Précision. Performance. Sécurité.',
          cta: 'Ouvrir le domaine →',
        },
        meta: {
          home: 'Ingénieur cybersécurité × IA × full-stack',
          cyber: 'Ingénierie en cybersécurité',
          ai: 'Ingénierie des systèmes IA',
          fullStack: 'Ingénierie full-stack',
          projects: 'Projets techniques',
          experience: 'Expérience et études',
          flappy: 'Flappy Bird',
          notFound: 'Page introuvable',
        },
      },
      domains: {
        cyber: {
          title: 'Ingénierie en cybersécurité',
          summary:
            'Architecture secure-by-design, patterns zero-trust, modélisation des menaces et pipelines de durcissement production.',
          tags: {
            threat: 'Modélisation des menaces',
            oidc: 'OAuth2/OIDC',
            jwt: 'Rotation JWT',
            siem: 'SIEM',
            pipeline: 'CI/CD sécurisé',
          },
        },
        ai: {
          title: 'Ingénierie des systèmes IA',
          summary:
            'Plateformes RAG, orchestration LLM, indexation vectorielle, évaluation de la recherche et inférence faible latence.',
          tags: {
            rag: 'RAG',
            embeddings: 'Embeddings',
            vector: 'Base vectorielle',
            llmops: 'LLMOps',
            observability: 'Observabilité',
          },
        },
        fullStack: {
          title: 'Ingénierie full-stack',
          summary:
            'Architecture backend, contrats d’API, bases résilientes, workloads conteneurisés et livraison cloud scalable.',
          tags: {
            api: 'APIs distribuées',
            postgres: 'PostgreSQL',
            docker: 'Docker',
            caching: 'Caching',
            deployment: 'Déploiement',
          },
        },
      },
      cyberPage: {
        kicker: 'Ingénierie défensive',
        title: 'Ingénierie en cybersécurité',
        subtitle:
          'Architecture security-first centrée sur la réduction du risque, des contrôles d’accès résilients, l’observabilité et le durcissement.',
        capabilitiesTitle: 'Capacités sécurité',
        architectureTitle: 'Plan d’architecture',
        architectureSubtitle: 'Couches et contrôles représentatifs :',
        caseTitle: 'Cas de modélisation des menaces et durcissement',
        references: {
          title: 'Projets associés',
          subtitle: 'Consultez la fiche technique complète dans la section Projets.',
          items: {
            athack: '→ CTF AtHack 2025 — writeups et tooling (page Projets)',
          },
        },
        capabilities: [
          'Modélisation des menaces avec STRIDE et cartographie des abus',
          'Durcissement identitaire avec OAuth2 + OIDC et rotation des refresh tokens JWT',
          'Chiffrement au repos et en transit avec gestion des cycles de clés',
          'Contrôles pipeline sécurisés : SAST, scan de dépendances, policy checks, artefacts signés',
          'Télémétrie SIEM avec règles de détection et playbooks d’incident',
        ],
        architecture: {
          identity: {
            label: 'Couche identité',
            value: 'MFA, accès conditionnel, tokens courts, rôles stricts',
          },
          api: {
            label: 'Protection API',
            value: 'Rate limiting, validation de schéma, middleware d’authz, audit',
          },
          data: {
            label: 'Protection des données',
            value: 'Chiffrement champ par champ, coffre à secrets, sauvegardes immuables',
          },
          runtime: {
            label: 'Défense runtime',
            value: 'Scan de conteneurs, politiques runtime, alertes d’anomalies',
          },
        },
        cases: [
          {
            title: 'CTF AtHack 2025 — Pratique défensive et offensive',
            problem: 'Résoudre des défis concrets en web, crypto, forensics et reverse.',
            approach:
              'Chaînes d’exploitation, reverse engineering et workflows forensics sous contrainte de temps.',
            outcome: '2ᵉ place et documentation complète des solutions avec l’équipe ÉTS.',
          },
          {
            title: 'Pipeline sécurisé pour plateforme multi-services',
            problem: 'Hygiène de dépendances incohérente et absence de politiques de release centralisées.',
            approach:
              'Mise en place de gates policy-as-code, budgets de vulnérabilité, SBOM et promotion signée.',
            outcome: 'Réduction du délai d’exposition critique de jours à heures et conformité standardisée.',
          },
        ],
      },
      aiPage: {
        kicker: 'Infrastructure d’intelligence',
        title: 'Ingénierie des systèmes IA',
        subtitle:
          'Ingénierie de pipelines IA de bout en bout, axée sur des sorties ancrées, traçables et performantes.',
        architectureTitle: 'Architecture d’agent IA',
        architectureSubtitle: 'Flux technique de l’ingestion à l’évaluation :',
        performanceTitle: 'Optimisation des performances',
        labsTitle: 'Systèmes de laboratoire',
        labels: {
          system: 'Système',
          challenge: 'Défi',
          result: 'Résultat',
        },
        blocks: {
          ingestion: { name: 'Ingestion', detail: 'Parseurs de documents, stratégie de chunking, normalisation' },
          embedding: { name: 'Couche embeddings', detail: 'Pipelines batch avec gouvernance dimension/version' },
          retrieval: { name: 'Récupération', detail: 'Recherche hybride, reranking, filtres de pertinence' },
          orchestration: { name: 'Orchestration', detail: 'Graphe d’agents, appels d’outils, garde-fous, retries' },
          generation: { name: 'Génération', detail: 'Templates de prompts, sorties structurées, vérifs de citations' },
          evaluation: { name: 'Évaluation', detail: 'Dashboards qualité/latence, checks d’hallucinations' },
        },
        performance: [
          'Optimisation du budget tokens avec compaction et contextes par classe de requête',
          'Couches de cache pour embeddings et candidats afin de contrôler la latence p95',
          'Exécution parallèle des recherches et outils pour augmenter le throughput',
          'File d’attente asynchrone pour l’indexation et l’évaluation lourdes',
        ],
        labs: [
          {
            title: 'Agent support avec RAG + tooling',
            system: 'Index de connaissances, embeddings en base vectorielle, appels de fonctions cadrés.',
            challenge: 'Concilier rapidité de réponse et fiabilité des citations.',
            result: 'Amélioration du taux de réponses ancrées et baisse de 34% de la latence médiane.',
          },
          {
            title: 'Copilote d’intelligence Ops',
            system: 'Résumé d’événements, explication d’anomalies, recommandations de runbooks.',
            challenge: 'Maintenir un format de sortie déterministe sous charge variable.',
            result: 'Réduction du temps de triage et adoption accrue des runbooks.',
          },
        ],
        references: {
          title: 'Projets associés',
          subtitle: 'Accédez directement aux fiches détaillées dans la section Projets.',
          items: {
            louis: '→ Louis AI (page Projets)',
            cyframeRag: '→ CyFrame RAG (page Projets)',
            cyframeDev: '→ CyFrame AI Dev (page Projets)',
            evalHarness: '→ Harness éval R&D (page Projets)',
            harborBlog: '→ Harbor Blog RAG (page Projets)',
          },
        },
      },
      aiProjects: {
        title: 'Portefeuille de systèmes IA',
        badge: 'Orchestration-first',
        subtitle:
          'Des systèmes qui combinent retrieval, logique décisionnelle, extraction structurée et pricing.',
        emphasisTitle: 'Principe clé :',
        emphasisText: 'Ce n’est pas un chatbot — c’est un moteur d’orchestration pour des workflows réels.',
        labels: {
          capabilities: 'Capacités',
          stack: 'Stack technique',
          emphasis: 'Point clé',
        },
        louis: {
          title: 'Louis AI — Agent autonome pour Harbor',
          summary:
            'Moteur d’orchestration IA stateful pour une marketplace, avec conversation orientée demande et extraction structurée.',
          capabilities: [
            'IA conversationnelle orientée demande et orchestration multi-phase',
            'Extraction structurée avec mapping d’options au niveau champ',
            'Matching sémantique via embeddings',
            'Fallbacks et prévention des hallucinations',
            'Intégration d’un algorithme de pricing en coins',
            'Streaming SSE en temps réel',
            'État de session persistant (MongoDB)',
            'Optimisations pour faible latence',
            'Alignement de persona pour l’UX psychologique',
            'Embeddings BGE-M3 + OpenAI',
            'Docker + déploiement Render',
          ],
          emphasis: 'Pas un chatbot : un moteur d’orchestration avec retrieval, décision, extraction et pricing.',
        },
        cyframeRag: {
          title: 'Assistant IA CyFrame — RAG Enterprise',
          summary:
            'Assistant IA intégré à un ERP legacy ASP + IIS avec recherche documentaire multilingue sécurisée.',
          capabilities: [
            'Pipeline d’indexation Python avec ingestion SVN',
            'Stockage vectoriel persistant et sécurisé',
            'Recherche multilingue (FR/EN/ES)',
            'Backend IA découplé intégré au frontend legacy',
            'Système de retrieval prêt production',
            'Posture enterprise-safe',
          ],
          emphasis: 'Modernisation IA d’un legacy tout en respectant les frontières de sécurité.',
        },
        cyframeDev: {
          title: 'Assistant IA développeur CyFrame',
          summary:
            'Assistant dédié aux devs pour la récupération de code et documentation.',
          capabilities: [
            'Retrieval de code et docs internes',
            'Q&A technique contextuel',
            'Intégration potentielle aux dépôts internes',
            'Gain de productivité pour les équipes',
          ],
          emphasis: 'Augmentation IA pour l’ingénierie : debug, onboarding, documentation.',
        },
        evalHarness: {
          title: 'Harness d’évaluation IA R&D',
          summary:
            'Module indépendant pour benchmarker des systèmes RAG avec une méthodologie scientifique.',
          capabilities: [
            "Mesure Recall{'@'}K et benchmarking de latence",
            'Comparaison BM25, vectoriel, hybride et reranking',
            'Framework d’expérimentation baseline',
            'Approche d’évaluation reproductible',
          ],
          emphasis: 'Ingénierie IA guidée par les métriques et les performances.',
        },
      },
      fullStackPage: {
        kicker: 'Ingénierie de production',
        title: 'Ingénierie full-stack',
        subtitle:
          'Livraison du cycle complet, de l’architecture et des contrats d’API au déploiement conteneurisé et à la scalabilité.',
        productionTitle: 'Décisions de déploiement en production',
        labels: {
          decision: 'Décision',
          impact: 'Impact',
        },
        pillars: {
          backend: {
            title: 'Architecture backend',
            details: 'Frontières de services, workflows async, intégration événementielle, isolation des pannes.',
          },
          api: {
            title: 'Conception d’API',
            details: 'Contrats typés, endpoints idempotents, pagination et versioning.',
          },
          data: {
            title: 'Couche données',
            details: 'Choix relationnel/NoSQL, stratégie d’index, transactions et migrations.',
          },
          devops: {
            title: 'DevOps + Delivery',
            details: 'Services Docker, CI/CD orchestré, quality gates, déploiement progressif.',
          },
          scaling: {
            title: 'Scalabilité',
            details: 'Scale horizontal, cache, backpressure de files, observabilité production.',
          },
        },
        production: [
          {
            title: 'Plateforme API à fort trafic',
            decision: 'Ajout d’un cache read-through et de files async pour opérations coûteuses.',
            impact: 'Stabilisation de la latence p95 et réduction de la pression DB.',
          },
          {
            title: 'Workflow de livraison conteneurisé',
            decision: 'Normalisation des builds multi-stage avec scan sécurité et promotion.',
            impact: 'Accélération des releases tout en renforçant la cohérence runtime.',
          },
        ],
      },
      projectsPage: {
        kicker: 'Portefeuille d’ingénierie',
        title: 'Projets à forte profondeur technique',
        subtitle:
          'Chaque projet met en avant le problème, l’architecture, la stack, les contraintes techniques et les résultats.',
        labels: {
          problem: 'Problème',
          approach: 'Approche',
          architecture: 'Architecture',
          stack: 'Stack',
          outcome: 'Résultat',
          challenges: 'Défis techniques',
          visuals: 'Visuels',
          related: 'Référence',
          repository: 'Dépôt',
          demo: 'Démo en ligne',
        },
        flappy: {
          title: 'Bonus : Flappy Bird',
          subtitle: 'Faites une pause et jouez au mini-jeu, puis revenez au portfolio.',
          play: 'Jouer à Flappy Bird',
          back: 'Retour au portfolio',
        },
      },
      projectsDetailed: {
        categories: {
          cyber: 'Cybersécurité',
          ai: 'Ingénierie IA',
          fullStack: 'Full-Stack',
          research: 'R&D',
          infrastructure: 'Infrastructure',
        },
        references: {
          aiPage: '→ Page Ingénierie IA',
          cyberPage: '→ Page Cybersécurité',
          cyframeExp: '→ CyFrame (page Expérience)',
          harborExp: '→ Harbor (page Expérience)',
        },
        athack: {
          name: 'AtHack CTF 2025',
          problem: 'Participer à un CTF réel avec contraintes de temps et sécurité.',
          architecture: 'Analyse, exploit chains et writeups sur plusieurs domaines techniques.',
          challenges: [
            'Exploitation web sous pression',
            'Crypto/forensics avec peu de contexte',
            'Reverse binaire sans documentation',
          ],
          outcome: '2ᵉ place et documentation complète des solutions.',
          visuals: 'Scoreboard CTF + notes de challenges',
        },
        mcgill: {
          name: 'Hackathon McGill 2023 — Simulation Hohmann',
          problem: 'Expliquer un transfert orbital via une simulation interactive.',
          architecture: 'React + p5.js + couche de paramètres physiques.',
          challenges: [
            'Stabilité de la simulation avec inputs live',
            'Rendre la physique compréhensible visuellement',
          ],
          outcome: 'Expérience éducative interactive pour l’orbite.',
          visuals: 'UI de simulation + trajectoires',
        },
        flow: {
          name: 'Event Flow Planner',
          problem: 'Planifier des événements multi-étapes avec authentification.',
          architecture: 'React + Spring Boot + JWT + base relationnelle.',
          challenges: [
            'Contrôles d’accès sécurisés',
            'Cohérence des timelines lors des modifications',
          ],
          outcome: 'Plateforme fonctionnelle de planification sécurisée.',
          visuals: 'Timeline UI + dashboards',
        },
        portfolio: {
          name: 'Plateforme portfolio d’ingénierie',
          problem: 'Créer un portfolio performant qui reflète une expertise système.',
          architecture:
            'Frontend orienté composants + modèle de contenu structuré + séparation des domaines + delivery optimisé.',
          challenges: [
            'Équilibrer la profondeur technique et la lisibilité',
            'Concevoir des layouts réutilisables pour les domaines',
            'Maintenir un style futuriste mais professionnel',
          ],
          outcome:
            'Portfolio moderne avec positionnement clair en cybersécurité, IA et full-stack.',
          visuals: 'Wireframes de page + captures UI',
        },
        vocal: {
          name: 'Application de chat vocal',
          problem: 'Permettre une communication vocale temps réel légère.',
          architecture: 'Client Kotlin + streaming audio via sockets.',
          challenges: [
            'Réduction de latence audio',
            'Gestion des connexions instables',
          ],
          outcome: 'Prototype fonctionnel de chat vocal.',
          visuals: 'UI + notes de connexion',
        },
        louis: {
          name: 'Louis AI — Moteur d’orchestration autonome',
          problem: 'Mapper une demande utilisateur vers des services avec extraction et pricing.',
          architecture: 'Orchestration stateful + embeddings + extraction + pricing + SSE.',
          challenges: [
            'Limiter les hallucinations en sortie structurée',
            'Garder la latence basse avec orchestration multi-phase',
            'Maintenir la cohérence d’état',
          ],
          outcome: 'Agent d’orchestration pour marketplace de services.',
          visuals: 'Flow agent + états d’orchestration',
        },
        harborBlog: {
          name: 'Harbor Blog RAG System',
          problem:
            'Répondre aux questions informatives (blog + plateforme) sans déclencher la pipeline de matching.',
          architecture:
            'Intent maître → isRagWorthy → branche RAG → recherche sémantique → synthèse LLM → sources.',
          challenges: [
            'Séparer les intentions transactionnelles vs informatives',
            'Optimiser chunking, overlap et seuil de confiance',
            'Mettre en place reindexing et contrôle des coûts',
          ],
          outcome:
            'RAG informatif prêt production avec fallback sécurisé et attribution des sources.',
          visuals: 'Diagramme d’intention + flow RAG',
        },
        svnManager: {
          name: 'SVNManager — Modernisation d’un système de patch legacy',
          problem: 'Remplacer PatchHelper par un workflow moderne et maintenable.',
          architecture:
            'Workflow de patch management modernisé avec meilleure fiabilité de déploiement.',
          challenges: [
            'Migration d’une infra vieillissante sans interruption',
            'Réduction de la friction pour les développeurs',
            'Architecture plus propre et maintenable',
          ],
          outcome:
            'Modernisation d’infrastructure ayant permis une intégration IA scalable ensuite.',
          visuals: 'Carte de migration + pipeline de déploiement',
        },
        cyframeRag: {
          name: 'Assistant RAG enterprise CyFrame',
          problem: 'Rechercher une documentation multilingue dans un ERP legacy.',
          architecture: 'Ingestion Python + DB vectorielle + backend IA + intégration IIS/ASP.',
          challenges: [
            'Frontières d’accès sécurisées',
            'Retrieval fiable FR/EN/ES',
          ],
          outcome: 'Intégration RAG enterprise pour productivité interne.',
          visuals: 'Pipeline RAG + intégration UI',
        },
        cyframeDev: {
          name: 'Assistant IA développeur CyFrame',
          problem: 'Accélérer le debug et la recherche documentaire des devs.',
          architecture: 'Retrieval code/docs + Q&A contextuel.',
          challenges: [
            'Maintenir un contexte précis',
            'Fusionner plusieurs sources internes',
          ],
          outcome: 'Assistant interne pour améliorer la vélocité.',
          visuals: 'Diagramme de workflow dev',
        },
        evalHarness: {
          name: 'Harness d’évaluation IA R&D',
          problem: 'Benchmarker des stratégies RAG avec métriques fiables.',
          architecture: 'Comparaison BM25, vectoriel, hybride, reranking.',
          challenges: [
            'Comparaisons justes et reproductibles',
            "Mesurer latence et Recall{'@'}K",
          ],
          outcome: 'Plateforme de benchmark pour choix de retrieval.',
          visuals: 'Dashboards d’évaluation + matrice d’expériences',
        },
      },
      theme: {
        dark: 'Sombre',
        light: 'Clair',
      },
      language: {
        toggle: 'Changer de langue',
      },
      experiencePage: {
        kicker: 'Carrière & études',
        title: 'Expérience et études',
        subtitle: 'Expériences chez CyFrame et Harbor, ainsi que mon parcours académique.',
        workTitle: 'Expérience professionnelle',
        educationTitle: 'Études',
        referencesLabel: 'Références',
        stacks: {
          title: 'Stacks que j’utilise et maîtrise',
          subtitle: 'Technologies appliquées en backend, frontend et plateforme.',
          items: {
            node: 'Node.js',
            express: 'Express.js',
            react: 'React',
            vue: 'Vue.js',
            python: 'Python',
            java: 'Java',
            spring: 'Spring Boot',
            mongodb: 'MongoDB',
            mysql: 'MySQL',
            oracle: 'OracleDB',
            aws: 'AWS',
            tailwind: 'Tailwind CSS',
            chakra: 'Chakra UI',
          },
        },
        work: {
          cyframe: {
            company: 'CyFrame',
            role: 'Développeur Full-Stack',
            timeline: '2023',
            summary:
              'Modernisation d’applications legacy, outils internes de gestion et migration des workflows de patch.',
            highlights: {
              0: 'Migration du patch management de VSS vers TortoiseSVN avec tracking automatisé.',
              1: 'Refonte de l’application Punch pour performance et ergonomie.',
              2: 'UI modernisée réduisant les temps de chargement de 30%.',
            },
            stack: 'Stack : ExpressJS, Node.js, OracleDB, Python, SVN, Shell, ASP Classic',
          },
          harbor: {
            company: 'Harbor.ca',
            role: 'Backend & plateforme',
            timeline: '2024',
            summary:
              'Architecture backend scalable, optimisation des données et APIs sécurisées pour une marketplace.',
            highlights: {
              0: 'Conception des services backend et logique métier de matching.',
              1: 'APIs sécurisées pour auth, messaging et notifications.',
              2: 'Outils admin et analytics pour les opérations.',
            },
            stack: 'Stack : ExpressJS, React, ChakraUI, MongoDB, APIs REST',
          },
        },
        education: {
          andre: {
            school: 'Cégep André-Laurendeau',
            program: 'Techniques de l’informatique (Back-End & Full-Stack)',
            summary:
              'Formation pratique en développement full-stack, systèmes backend et architecture sécurisée.',
            highlights: {
              0: 'Web Angular/React/Thymeleaf et mobile Flutter.',
              1: 'Spring Boot, Python, Kotlin, JWT, bases MySQL/H2.',
              2: 'Projets capstone orientés production.',
            },
            date: 'Diplômé : mai 2024',
          },
          ets: {
            school: 'ÉTS — École de technologie supérieure',
            program: 'Baccalauréat en génie logiciel',
            summary:
              'Conception logicielle avancée, IA, cybersécurité et infonuagique.',
            highlights: {
              0: 'Architecture logicielle avancée et systèmes distribués.',
              1: 'Spécialisation IA et cybersécurité.',
              2: 'Infonuagique et fiabilité des systèmes.',
            },
            date: 'Début : automne 2024',
          },
        },
        references: {
          aiDev: '→ CyFrame AI Developer (page IA)',
          aiHarness: '→ Harness éval IA R&D (page IA)',
          aiRag: '→ Assistant RAG CyFrame (page IA)',
          svnManager: '→ SVNManager (page Projets)',
          louis: '→ Louis AI (page IA)',
          harborBlog: '→ Harbor Blog RAG (page Projets)',
        },
        cvButton: 'Télécharger mon CV',
      },
    }
  }
});

export default i18n;
